import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
const ENV_DEV = import.meta.env.VITE_ENV_DEV;

interface PriceDetails {
  price: number;
  negotiable: boolean;
  reservedNegotation: boolean;
}
interface IncludeDetails {
  withCadastralData: boolean;
  withPhotos: boolean;
  withLayout: boolean;
  withVideo: boolean;
  withPanoramicPhotos: boolean;
  withVirtualTour: boolean;
  auction: boolean;
  auctionExpired?: boolean;
}
interface VetrinaDetails {
  number: number;
  position: number;
}
interface StatusDetails {
  id: number;
  name: string;
}
interface CategoryDetails {
  id: number;
  name: string;
}
interface AddressDetails {
  provinciaCode: string;
  provinciaName: string;
  provinciaSigla: string;
  comuneCode: string;
  comuneName: string;
  frazioneCode: number;
  frazioneName: string;
  quartiereCode: number;
  quartiereName: string;
}
interface TipologyDetails {
  id: number;
  name: string;
  seoString: string;
}
interface ContractDetails {
  id: number;
  name: string;
}
interface DistanceDetails {
  id: number;
  name: string;
  mt: number;
}
export interface Immobile {
  id: number;
  agencyId: number;
  name: string;
  link: string;
  description: string;
  ref: string;
  sqmt: number;
  seoString: string;
  exclusive: boolean;
  rooms: number;
  bathrooms: number;
  premises: number;
  priceDetails: PriceDetails;
  includeDetails: IncludeDetails;
  vetrinaDetails: VetrinaDetails;
  statusDetails: StatusDetails;
  categoryDetails: CategoryDetails;
  addressDetails: AddressDetails;
  tipologyDetails: TipologyDetails;
  contractDetails: ContractDetails;
  distances: DistanceDetails[];
  mainImage: string | null;
}
interface GetPropertiesParams {
  limit?: number;
  offset?: number;
  withCount?: boolean;
  portal?: number;
  vetrina?: number;
}
interface ApiResponse {
  count: number;
  data: Immobile[];
  withCount: boolean;
}

export const useGetPropertiesVetrina = (agencyId: number, params?: GetPropertiesParams) => {
  const defaultLimit = params?.limit ?? 1000000;
  const defaultOffset = params?.offset ?? 0;
  const defaultWithCount = params?.withCount ?? true;
  const defaultPortal = params?.portal ?? 56;
  const defaultVetrina = params?.vetrina ?? 2;

  const [properties, setProperties] = useState<Immobile[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentLimit, setCurrentLimit] = useState<number>(defaultLimit);
  const [currentOffset, setCurrentOffset] = useState<number>(defaultOffset);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post<ApiResponse>(`${API_BASE_URL}/properties`, {
          agencyId: agencyId,
          limit: currentLimit,
          offset: currentOffset,
          withCount: defaultWithCount,
          status: [2, 3, 9, 11],
          filters: { vetrina: defaultVetrina },
          portal: defaultPortal,
        });

        if (ENV_DEV === true) {
          console.log("Response from /properties:", response.data);
        }

        const apiData = response.data;

        const formattedProperties: Immobile[] = apiData.data.map((property) => {
            return {
              id: property.id,
              agencyId: property.agencyId,
              name: `Immobile ${property.id}`,
              link: property.link || `#`,
              description: property.description || "Descrizione non disponibile.",
              ref: property.ref,
              sqmt: property.sqmt,
              seoString: property.seoString,
              exclusive: property.exclusive,
              rooms: property.rooms,
              bathrooms: property.bathrooms,
              premises: property.premises,
              priceDetails: property.priceDetails || {
                price: 0,
                negotiable: false,
                reservedNegotation: false,
              },
              includeDetails: property.includeDetails,
              vetrinaDetails: property.vetrinaDetails,
              statusDetails: property.statusDetails,
              categoryDetails: property.categoryDetails,
              addressDetails: property.addressDetails || {
                provinciaCode: "",
                provinciaName: "",
                provinciaSigla: "",
                comuneCode: "",
                comuneName: "",
                frazioneCode: 0,
                frazioneName: "",
                quartiereCode: 0,
                quartiereName: "",
              },
              tipologyDetails: property.tipologyDetails,
              contractDetails: property.contractDetails,
              distances: property.distances || [],
              mainImage: property.mainImage || "",
            };
          });
        setProperties(formattedProperties);
        setTotalCount(apiData.count);
      } catch (err) {
        console.error("Errore nel caricamento delle proprietà:", err);
        setError("Errore nel caricamento delle proprietà");
        setProperties([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    if (agencyId) {
      fetchProperties();
    } else {
      setLoading(false);
      setProperties([]);
      setTotalCount(0);
    }
  }, [agencyId, currentLimit, currentOffset, defaultWithCount, defaultPortal]);

  const goToNextPage = useCallback(() => {
    if (currentOffset + currentLimit < totalCount) {
      setCurrentOffset(prevOffset => prevOffset + currentLimit);
    }
  }, [currentLimit, currentOffset, totalCount]);

  const goToPrevPage = useCallback(() => {
    setCurrentOffset(prevOffset => Math.max(0, prevOffset - currentLimit));
  }, [currentLimit]);

  const changeLimit = useCallback((newLimit: number) => {
    setCurrentLimit(newLimit);
    setCurrentOffset(0);
  }, []);

  const goToPage = useCallback((pageNumber: number) => {
    if (pageNumber > 0) {
      setCurrentOffset((pageNumber - 1) * currentLimit);
    }
  }, [currentLimit]);

  return {
    properties,
    totalCount,
    loading,
    error,
    limit: currentLimit,
    offset: currentOffset,
    goToNextPage,
    goToPrevPage,
    changeLimit,
    goToPage,
  };
};